/**
 * Calculs d'affichage de la section Ventes.
 *
 * L'API renvoie les totaux BRUTS des trois fenêtres (période, précédente, an
 * dernier) ; tout ce qui est variation se calcule ici, une seule fois, pour
 * toutes les tuiles et le tableau des produits.
 */

import type { SalesDailyPoint, SubscriptionDailyPoint } from '@shared/types/sales';

/**
 * Variation relative, en %, arrondie à une décimale.
 *
 * `null` contre une référence nulle : une variation contre zéro n'existe pas,
 * la forcer à +100 % ou à l'infini inventerait un chiffre.
 */
export function relativeChange(current: number, reference: number): number | null {
  if (reference === 0) return null;
  return Math.round(((current - reference) / Math.abs(reference)) * 1000) / 10;
}

// -----------------------------------------------------------------------------
// Séries comparées
// -----------------------------------------------------------------------------

export interface ComparedPoint {
  /** Premier jour du seau, dans la période affichée. */
  date: string;
  /** Premier jour du seau correspondant, dans la période de référence. */
  referenceDate: string | null;
  current: number;
  /** `null` quand la référence est plus courte (29 février). */
  reference: number | null;
}

/**
 * Taille des seaux, en jours.
 *
 * Au jour près jusqu'à deux mois ; au-delà, 365 points par courbe deviennent
 * une bande de bruit où deux séries superposées ne se lisent plus. On passe
 * alors à la semaine, puis à 30 jours au-delà d'un an.
 */
export function bucketSizeFor(days: number): number {
  if (days <= 62) return 1;
  if (days <= 366) return 7;
  return 30;
}

/**
 * Aligne une série sur sa référence, par RANG et non par date.
 *
 * Le 1er jour de la période contre le 1er jour de la référence, le 2e contre le
 * 2e… C'est la seule façon de superposer « septembre » et « août » (dont les
 * dates diffèrent toutes) ou deux années dont l'une a un 29 février.
 *
 * Les seaux partent du DÉBUT de la période : le dernier peut être incomplet,
 * mais sa référence l'est exactement autant — la comparaison reste juste, même
 * si la courbe plonge sur le dernier point.
 */
export function compareSeries<T extends { date: string }>(
  current: readonly T[],
  reference: readonly T[],
  value: (point: T) => number,
  bucketSize: number,
  /**
   * `sum` pour un FLUX (CA, commandes : la semaine vaut la somme de ses
   * jours) ; `last` pour un STOCK (abonnés actifs : la semaine vaut son
   * dernier soir — additionner sept soirs compterait chaque abonné sept fois).
   */
  aggregate: 'sum' | 'last' = 'sum',
): ComparedPoint[] {
  const reduce = (slice: readonly T[]): number => {
    if (aggregate === 'last') {
      const last = slice[slice.length - 1];
      return last ? value(last) : 0;
    }
    return round2(slice.reduce((sum, point) => sum + value(point), 0));
  };

  const points: ComparedPoint[] = [];

  for (let start = 0; start < current.length; start += bucketSize) {
    const slice = current.slice(start, start + bucketSize);
    const refSlice = reference.slice(start, start + bucketSize);
    const first = slice[0];
    if (!first) continue;

    points.push({
      date: first.date,
      referenceDate: refSlice[0]?.date ?? null,
      current: reduce(slice),
      reference: refSlice.length > 0 ? reduce(refSlice) : null,
    });
  }

  return points;
}

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

/** Un point de CA total : boutique + abonnements. */
export interface TotalRevenuePoint {
  date: string;
  netRevenue: number;
}

/**
 * Additionne jour par jour le CA boutique et le CA abonnements.
 *
 * Les deux séries couvrent exactement la même fenêtre (mêmes bornes, même
 * fuseau, sans trous) : l'addition se fait par rang. Une série absente
 * (abonnements pas encore chargés) compte pour zéro. Les abonnements vendus
 * sur le site sont dans les deux séries : retirés une fois.
 */
export function combineDailyRevenue(
  shop: readonly SalesDailyPoint[],
  subscriptions: readonly SubscriptionDailyPoint[],
): TotalRevenuePoint[] {
  return shop.map((point, index) => ({
    date: point.date,
    netRevenue: round2(
      point.netRevenue + (subscriptions[index]?.netRevenue ?? 0) - (subscriptions[index]?.siteRevenue ?? 0),
    ),
  }));
}
